import { PALETTE, UNASSIGNED_COLOR, patternKeyFor } from "../model/assignmentColors.ts";
import { BASE_STYLE, PROBLEM_STYLE, SELECTION_FILL_COLOR } from "./mapStyles.ts";

/**
 * SVG definitions for the diagonal stripe pattern a sub-basin with a snow
 * zone gets instead of a plain fill (`pattern-snow-normal`/`-issue`,
 * referenced directly as a `fillColor` by subBasinMapStyle.ts's
 * `baseStyleFor` — a plain CSS class isn't an option here: `selected`
 * changes without remounting the `<GeoJSON>` layer, only via
 * `layer.setStyle(...)` on click/hover (onEachFeature.ts), and Leaflet's
 * SVG renderer only ever applies `options.className` once, at mount
 * (`_initPath`) — `_updateStyle`, what `setStyle` actually calls, never
 * touches it, so a class picked at mount would permanently keep winning
 * over every later `fillColor` a plain object update tries to set).
 * `pattern-selected` exists for the exact same "still needs to read as
 * snow" reason as `pattern-assign-*` (one per assignmentColors.ts palette
 * color/UNASSIGNED_COLOR) below it — selection always overrides the whole
 * fill (subBasinMapStyle.ts's `styleFor`), and a plain solid highlight
 * color would otherwise erase the snow stripe entirely for a selected
 * snow sub-basin, indistinguishable from a selected non-snow one. A
 * `<pattern>` is referenced by id from anywhere in the document (it
 * doesn't need to be in the same `<svg>` that uses it), so it's enough to
 * mount this block once, hidden, instead of injecting it inside the SVG
 * that Leaflet generates. The two fixed patterns' background color
 * repeats the `fillColor` of BASE_STYLE/PROBLEM_STYLE (mapStyles.ts) on
 * purpose — if those colors change, these two need updating too.
 */
export function SnowPatterns() {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
      <defs>
        <pattern
          id="pattern-snow-normal"
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
          patternTransform="rotate(45)"
        >
          <rect width="8" height="8" fill={BASE_STYLE.fillColor} />
          <line x1="0" y1="0" x2="0" y2="8" stroke="#ffffff" strokeWidth="3" strokeOpacity="0.6" />
        </pattern>
        <pattern
          id="pattern-snow-issue"
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
          patternTransform="rotate(45)"
        >
          <rect width="8" height="8" fill={PROBLEM_STYLE.fillColor} />
          <line x1="0" y1="0" x2="0" y2="8" stroke="#ffffff" strokeWidth="3" strokeOpacity="0.6" />
        </pattern>
        <pattern
          id="pattern-selected"
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
          patternTransform="rotate(45)"
        >
          <rect width="8" height="8" fill={SELECTION_FILL_COLOR} />
          <line x1="0" y1="0" x2="0" y2="8" stroke="#ffffff" strokeWidth="3" strokeOpacity="0.6" />
        </pattern>
        {[...PALETTE, UNASSIGNED_COLOR].map((color) => (
          <pattern
            key={color}
            id={`pattern-assign-${patternKeyFor(color)}`}
            patternUnits="userSpaceOnUse"
            width="8"
            height="8"
            patternTransform="rotate(45)"
          >
            <rect width="8" height="8" fill={color} />
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="8"
              stroke="#ffffff"
              strokeWidth="3"
              strokeOpacity="0.6"
            />
          </pattern>
        ))}
      </defs>
    </svg>
  );
}
